// src/services/mockDb.js

// Initial Seed Data
const initialBookings = [
  {
    id: "BK-1000",
    clientName: "Client 1",
    clientEmail: "client1@example.com",
    bookingDate: "2024-02-20",
    startTime: "10:00",
    endTime: "11:00",
    timeLabel: "10:00 AM - 11:00 AM",
    status: "confirmed",
    assignedEmployeeId: "EMP-1",
    assignedEmployeeName: "John Smith",
    createdAt: "2024-02-15T10:00:00Z",
    notes: "Looking for business consultation"
  },
  {
    id: "BK-1001",
    clientName: "Client 2",
    clientEmail: "client2@example.com",
    bookingDate: "2024-02-21",
    startTime: "14:00",
    endTime: "14:30",
    timeLabel: "02:00 PM - 02:30 PM",
    status: "cancelled",
    assignedEmployeeId: null,
    assignedEmployeeName: null,
    createdAt: "2024-02-16T11:00:00Z",
    notes: ""
  },
  {
    id: "BK-1002",
    clientName: "Client 3",
    clientEmail: "client3@example.com",
    bookingDate: "2024-02-22",
    startTime: "09:00",
    endTime: "10:00",
    timeLabel: "09:00 AM - 10:00 AM",
    status: "pending",
    assignedEmployeeId: null,
    assignedEmployeeName: null,
    createdAt: "2024-02-18T09:00:00Z",
    notes: ""
  },
  {
    id: "BK-1003",
    clientName: "Client 4",
    clientEmail: "client4@example.com",
    bookingDate: "2024-02-23",
    startTime: "15:00",
    endTime: "16:00",
    timeLabel: "03:00 PM - 04:00 PM",
    status: "unanswered",
    assignedEmployeeId: null,
    assignedEmployeeName: null,
    createdAt: "2024-02-19T14:30:00Z",
    notes: ""
  }
];

const initialEmployees = [
  {
    id: "EMP-1",
    fullName: "John Smith",
    category: "Business Consultant",
    email: "john.smith@company.com",
    phone: "+61 400 123 456",
    isBlocked: false,
    isActive: true,
    password: "password123", // In real app, never store plain text
    createdAt: "2023-01-01T00:00:00Z"
  },
  {
    id: "EMP-2",
    fullName: "Sarah Johnson",
    category: "Digital Marketing",
    email: "sarah@company.com",
    phone: "+61 400 234 567",
    isBlocked: false,
    isActive: true,
    password: "password123",
    createdAt: "2023-02-01T00:00:00Z"
  },
  {
    id: "EMP-3",
    fullName: "Michael Brown",
    category: "Financial Advisor",
    email: "michael@company.com",
    phone: "+61 400 345 678",
    isBlocked: true,
    isActive: false,
    password: "password123",
    createdAt: "2023-03-01T00:00:00Z"
  }
];

const initialAvailability = [
  {
    employeeId: "EMP-1",
    date: "2024-02-20",
    isActive: true, // Employee was active on this day
    slots: [
      { start: "09:00", end: "09:30", available: true },
      { start: "09:30", end: "10:00", available: true },
      { start: "10:00", end: "10:30", available: false }, // Booked by BK-1000
      { start: "10:30", end: "11:00", available: false }, // Booked by BK-1000
      { start: "11:00", end: "11:30", available: true },
      // ... assume others open
    ]
  }
];

const initialQuotations = [];

// In-Memory Storage
class MockDatabase {
  constructor() {
    this.bookings = [...initialBookings];
    this.employees = [...initialEmployees];
    this.availability = [...initialAvailability];
    this.quotations = [...initialQuotations];
  }

  // --- Helpers ---
  delay(ms = 500) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // --- Auth ---
  async login(role, email, password) {
    await this.delay();
    if (role === 'admin') {
      // Hardcoded admin
      if (email === 'admin@aspiretaxbook.com' && password === 'AdminPassword@123') {
        return {
          token: "mock-admin-token-123",
          user: { id: "ADMIN-1", name: "Super Admin", email, role: 'admin' }
        };
      }
    } else {
      const emp = this.employees.find(e => e.email === email && e.password === password);
      if (emp) {
        if (emp.isBlocked) throw new Error("Account is blocked.");
        return {
          token: `mock-emp-token-${emp.id}`,
          user: { id: emp.id, name: emp.fullName, email, role: 'employee' }
        };
      }
    }
    throw new Error("Invalid credentials");
  }

  // --- Bookings ---
  async getBookings() {
    await this.delay();
    return [...this.bookings];
  }

  async getBookingById(id) {
    await this.delay();
    return this.bookings.find(b => b.id === id);
  }

  async updateBooking(id, payload) {
    await this.delay();
    const index = this.bookings.findIndex(b => b.id === id);
    if (index === -1) throw new Error("Booking not found");
    this.bookings[index] = { ...this.bookings[index], ...payload };
    return this.bookings[index];
  }

  // --- Employees ---
  async getEmployees() {
    await this.delay();
    return [...this.employees];
  }

  async createEmployee(data) {
    await this.delay();
    const newEmp = {
      id: `EMP-${Date.now()}`,
      isActive: true,
      isBlocked: false,
      createdAt: new Date().toISOString(),
      ...data
    };
    this.employees.push(newEmp);
    return newEmp;
  }

  async updateEmployee(id, data) {
    await this.delay();
    const idx = this.employees.findIndex(e => e.id === id);
    if (idx !== -1) {
      this.employees[idx] = { ...this.employees[idx], ...data };
      return this.employees[idx];
    }
    throw new Error("Employee not found");
  }

  async deleteEmployee(id) {
    await this.delay();
    this.employees = this.employees.filter(e => e.id !== id);
    return true;
  }

  async toggleEmployeeBlock(id, isBlocked) {
    return this.updateEmployee(id, { isBlocked });
  }

  // --- Availability ---
  async getAvailability(employeeId, date) {
    await this.delay();
    // Return existing or default structure
    const found = this.availability.find(a => a.employeeId === employeeId && a.date === date);
    if (found) return found;

    // Default structure: 9 AM to 5 PM, 30 min slots
    const slots = [];
    let startH = 9;
    let startM = 0;
    for (let i = 0; i < 16; i++) { // 16 slots of 30 mins = 8 hours
        const startStr = `${String(startH).padStart(2, '0')}:${String(startM).padStart(2, '0')}`;
        // increment
        startM += 30;
        if (startM === 60) { startM = 0; startH++; }
        const endStr = `${String(startH).padStart(2, '0')}:${String(startM).padStart(2, '0')}`;
        
        slots.push({ start: startStr, end: endStr, available: false }); // Default false until set? Or true? Req says "Pick availability", so maybe starts empty.
    }
    
    return {
        employeeId,
        date,
        isActive: true, // Default active? Or inactive? Let's say true but no slots selected.
        slots
    };
  }

  async saveAvailability({ employeeId, date, slots, isActive }) {
    await this.delay();
    const idx = this.availability.findIndex(a => a.employeeId === employeeId && a.date === date);
    if (idx !== -1) {
        this.availability[idx] = { employeeId, date, slots, isActive };
    } else {
        this.availability.push({ employeeId, date, slots, isActive });
    }
    return { employeeId, date, slots, isActive };
  }

  // --- Quotations ---
  async getQuotations() {
    await this.delay();
    // Sort buy createdAt desc
    return [...this.quotations].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }

  async getQuotationById(id) {
    await this.delay();
    return this.quotations.find(q => q.id === id);
  }

  async createQuotation(data) {
    await this.delay();
    const newQuote = {
        id: `QT-${1000 + this.quotations.length + 1}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastPdfGeneratedAt: null,
        status: "draft",
        ...data
    };
    this.quotations.push(newQuote);
    return newQuote;
  }

  async updateQuotation(id, data) {
    await this.delay();
    const idx = this.quotations.findIndex(q => q.id === id);
    if (idx !== -1) {
        this.quotations[idx] = { 
            ...this.quotations[idx], 
            ...data,
            updatedAt: new Date().toISOString()
        };
        return this.quotations[idx];
    }
    throw new Error("Quotation not found");
  }

  async markDownloaded(id) {
      await this.delay();
      const idx = this.quotations.findIndex(q => q.id === id);
      if (idx !== -1) {
          this.quotations[idx].status = "downloaded";
          this.quotations[idx].lastPdfGeneratedAt = new Date().toISOString();
          return this.quotations[idx];
      }
      throw new Error("Quotation not found");
  }

  async deleteQuotation(id) {
      await this.delay();
      this.quotations = this.quotations.filter(q => q.id !== id);
      return true;
  }

  async sendQuotation(id) {
    await this.delay();
    const idx = this.quotations.findIndex(q => q.id === id);
    if (idx !== -1) {
        this.quotations[idx].status = "sent";
        return this.quotations[idx];
    }
    throw new Error("Quotation not found");
  }
}

// Singleton
const mockDb = new MockDatabase();
export default mockDb;
